import { SxProps, Theme } from '@mui/material';
import { DUTIES_COLOURS } from './theme';

// ============================================================================
// Table Container Styles
// ============================================================================

/**
 * Loading state container
 * Centres loading spinner with appropriate spacing
 */
export const loadingContainerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px'
};

/**
 * Empty state container
 * Centres empty state message with appropriate spacing
 */
export const emptyStateContainerStyles: SxProps<Theme> = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: '200px'
};

// ============================================================================
// Table Header Styles
// ============================================================================

/**
 * Desktop table header row
 * Hidden on mobile, displays column headers
 */
export const tableHeaderRowStyles: SxProps<Theme> = {
  display: { xs: 'none', sm: 'flex' },
  gap: '12px',
  alignItems: 'center',
  padding: '8px 0',
  borderBottom: `1px solid ${DUTIES_COLOURS.borderMedium}`
};

/**
 * Table column header typography
 * Bold text for column headers
 */
export const columnHeaderTypographyStyles: SxProps<Theme> = {
  fontWeight: 600,
  color: DUTIES_COLOURS.primary
};

/**
 * Column header: Duty name (flexible width)
 */
export const dutyColumnHeaderStyles: SxProps<Theme> = {
  flex: '1 0 0',
  minWidth: 0
};

/**
 * Column header: Times Required (fixed width)
 */
export const timesColumnHeaderStyles: SxProps<Theme> = {
  width: '180px',
  flexShrink: 0
};

/**
 * Column header: No. People (fixed width)
 */
export const peopleColumnHeaderStyles: SxProps<Theme> = {
  width: '100px',
  flexShrink: 0
};

/**
 * Column header: Configure button (fixed width)
 */
export const configureColumnHeaderStyles: SxProps<Theme> = {
  width: '120px',
  flexShrink: 0
};

// ============================================================================
// Table Row Styles (Desktop)
// ============================================================================

/**
 * Desktop table row container
 * Hidden on mobile
 */
export const desktopRowStyles: SxProps<Theme> = {
  display: { xs: 'none', sm: 'flex' },
  gap: '12px',
  alignItems: 'center',
  width: '100%',
  backgroundColor: DUTIES_COLOURS.background,
  padding: '12px 0'
};

/**
 * Row divider between rows
 * Only shown on desktop
 */
export const rowDividerStyles: SxProps<Theme> = {
  display: { xs: 'none', sm: 'block' },
  borderBottom: `1px solid ${DUTIES_COLOURS.borderLight}`
};

// ============================================================================
// Table Cell Styles (Desktop)
// ============================================================================

/**
 * Duty name cell with icon
 */
export const dutyCellStyles: SxProps<Theme> = {
  flex: '1 0 0',
  minWidth: 0,
  display: 'flex',
  alignItems: 'center',
  gap: '8px'
};

/**
 * Duty icon styling
 */
export const dutyIconStyles: SxProps<Theme> = {
  fontSize: 24,
  color: DUTIES_COLOURS.secondary
};

/**
 * Duty name typography
 */
export const dutyNameTypographyStyles: SxProps<Theme> = {
  fontWeight: 400,
  color: DUTIES_COLOURS.primary
};

/**
 * Times Required cell
 */
export const timesCellStyles: SxProps<Theme> = {
  width: '180px',
  flexShrink: 0
};

/**
 * Times typography (allows multi-line)
 */
export const timesTypographyStyles: SxProps<Theme> = {
  whiteSpace: 'pre-line',
  color: DUTIES_COLOURS.primary
};

/**
 * No. People cell
 */
export const peopleCellStyles: SxProps<Theme> = {
  width: '100px',
  flexShrink: 0
};

/**
 * No. People typography
 */
export const peopleTypographyStyles: SxProps<Theme> = {
  whiteSpace: 'pre-line',
  color: DUTIES_COLOURS.secondary,
  lineHeight: '1.5'
};

/**
 * Configure button cell
 */
export const configureCellStyles: SxProps<Theme> = {
  width: '120px',
  flexShrink: 0,
  display: 'flex',
  justifyContent: 'center'
};

/**
 * Configure icon button styling
 */
export const configureIconButtonStyles: SxProps<Theme> = {
  '&:hover': {
    backgroundColor: 'transparent'
  }
};

/**
 * Configure settings icon styling
 */
export const configureIconStyles: SxProps<Theme> = {
  color: DUTIES_COLOURS.secondary,
  '&:hover': {
    color: DUTIES_COLOURS.primary
  }
};

// ============================================================================
// Table Row Styles (Mobile)
// ============================================================================

/**
 * Mobile card-style row container
 * Only shown on mobile
 */
export const mobileRowStyles: SxProps<Theme> = {
  display: { xs: 'block', sm: 'none' },
  backgroundColor: DUTIES_COLOURS.background,
  padding: '12px 0',
  borderBottom: `1px solid ${DUTIES_COLOURS.borderLight}`
};

/**
 * Mobile row header (icon + name)
 */
export const mobileRowHeaderStyles: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  mb: 1
};

/**
 * Mobile duty icon styling
 */
export const mobileDutyIconStyles: SxProps<Theme> = {
  fontSize: 20,
  color: DUTIES_COLOURS.secondary
};

/**
 * Mobile duty name typography
 */
export const mobileDutyNameTypographyStyles: SxProps<Theme> = {
  fontWeight: 500,
  flex: 1,
  color: DUTIES_COLOURS.primary
};

/**
 * Mobile info section container (times + people)
 */
export const mobileInfoSectionStyles: SxProps<Theme> = {
  display: 'flex',
  gap: '16px',
  pl: '34px',
  mb: 1
};

/**
 * Mobile times section (flexible)
 */
export const mobileTimesSectionStyles: SxProps<Theme> = {
  flex: 1
};

/**
 * Mobile people section (fixed width)
 */
export const mobilePeopleSectionStyles: SxProps<Theme> = {
  minWidth: '80px'
};

/**
 * Mobile section label typography
 */
export const mobileSectionLabelStyles: SxProps<Theme> = {
  color: DUTIES_COLOURS.secondary,
  display: 'block'
};

/**
 * Mobile section value typography
 */
export const mobileSectionValueStyles: SxProps<Theme> = {
  whiteSpace: 'pre-line',
  color: DUTIES_COLOURS.primary
};

/**
 * Mobile configure button container
 */
export const mobileConfigureContainerStyles: SxProps<Theme> = {
  pl: '34px'
};

/**
 * Mobile configure icon button
 */
export const mobileConfigureIconButtonStyles: SxProps<Theme> = {
  minWidth: '100px'
};

/**
 * Mobile configure icon
 */
export const mobileConfigureIconStyles: SxProps<Theme> = {
  color: DUTIES_COLOURS.primary
};
